import React, { useState, useRef, useEffect } from 'react'
import { Camera, Settings, Info, Play, Pause } from 'lucide-react'
import Navigation from '../../../components/common/Navigation'
import ColorTracker from './ColorTracker'
import MagneticField from './MagneticField'

/**
 * Magnetic Field AR - Augmented Reality visualization of magnetic field lines
 * 
 * Features:
 * - Real-time color tracking (red/blue magnet poles)
 * - 3D magnetic field line rendering
 * - Coil detection and flux calculation
 * - Educational annotations
 */
const MagneticFieldAR = () => {
    // State
    const [isRunning, setIsRunning] = useState(false)
    const [cameraReady, setCameraReady] = useState(false)
    const [magnetDetected, setMagnetDetected] = useState(false)
    const [showFieldLines, setShowFieldLines] = useState(true)
    const [fieldLineDensity, setFieldLineDensity] = useState(20)
    const [statusText, setStatusText] = useState('Click Start to begin')

    // Refs
    const videoRef = useRef<HTMLVideoElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const overlayCanvasRef = useRef<HTMLCanvasElement>(null)
    const animationFrameRef = useRef<number>()

    // Tracking Smoothing
    const prevMagnetData = useRef({
        north: { x: 0, y: 0 },
        south: { x: 0, y: 0 },
        initialized: false
    })

    // Start camera
    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    facingMode: 'environment'
                }
            })

            if (videoRef.current) {
                videoRef.current.srcObject = stream
                videoRef.current.addEventListener('loadeddata', () => {
                    setCameraReady(true)
                    setStatusText('Camera ready! Show red/blue magnet')
                    startTracking()
                }, { once: true })
            }

            setIsRunning(true)
        } catch (error) {
            console.error('Camera error:', error)
            setStatusText('Camera access denied')
        }
    }

    // Stop camera
    const stopCamera = () => {
        if (videoRef.current?.srcObject) {
            const stream = videoRef.current.srcObject as MediaStream
            stream.getTracks().forEach(track => track.stop())
            videoRef.current.srcObject = null
        }

        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current)
        }

        setIsRunning(false)
        setCameraReady(false)
        setStatusText('Stopped')
        setMagnetDetected(false)
        prevMagnetData.current.initialized = false
    }

    // Helper: Linear Interpolation for smoothing
    const lerp = (start: number, end: number, factor: number) => {
        return start + (end - start) * factor
    }

    // Helper: Draw Arrow for Magnetic Vector
    const drawArrow = (ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number) => {
        const headLength = 8
        const dx = x2 - x1
        const dy = y2 - y1
        const angle = Math.atan2(dy, dx)

        ctx.beginPath()
        ctx.moveTo(x2, y2)
        ctx.lineTo(x2 - headLength * Math.cos(angle - Math.PI / 6), y2 - headLength * Math.sin(angle - Math.PI / 6))
        ctx.moveTo(x2, y2)
        ctx.lineTo(x2 - headLength * Math.cos(angle + Math.PI / 6), y2 - headLength * Math.sin(angle + Math.PI / 6))
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)'
        ctx.lineWidth = 1.5
        ctx.stroke()
    }

    // Helper: Draw Field Lines (Textbook Style)
    const drawFieldLines = (ctx: CanvasRenderingContext2D, north: { x: number, y: number }, south: { x: number, y: number }) => {
        const numLines = fieldLineDensity

        // Geometry
        const dx = south.x - north.x
        const dy = south.y - north.y
        const dist = Math.sqrt(dx * dx + dy * dy)
        if (dist < 10) return

        const centerX = (north.x + south.x) / 2
        const centerY = (north.y + south.y) / 2

        // Unit vectors
        const uX = dx / dist, uY = dy / dist // Axis direction
        const pX = -uY, pY = uX              // Perpendicular direction

        const magnetRadius = 25 // Approximate "thickness" of magnet to start lines

        // --- 1. SIDE LOOPS (Midplane Seeding) ---
        // Lines starting from perpendicular bisector and going to both poles
        // This creates standard N->S arcs
        const sideCount = Math.floor(numLines * 0.8)

        for (let i = 0; i < sideCount; i++) {
            // Distance from center along perpendicular axis
            // Non-linear spacing to put more lines closer to magnet
            const t = (i + 1) / sideCount
            const offset = magnetRadius + (t * t * dist * 1.5)

            // Draw Top Side
            drawContinuousLine(ctx, centerX + pX * offset, centerY + pY * offset, north, south)

            // Draw Bottom Side
            drawContinuousLine(ctx, centerX - pX * offset, centerY - pY * offset, north, south)
        }

        // --- 2. POLAR FANS (End caps) ---
        // Lines exiting N and entering S at the ends
        const fanCount = Math.floor(numLines * 0.5)
        const fanRadius = 20

        // North Fan (Outward)
        for (let i = 0; i < fanCount; i++) {
            // Angle spread around the axis (-90 to +90 degrees relative to axis)
            const angleOffset = ((i / (fanCount - 1)) - 0.5) * Math.PI // -PI/2 to PI/2

            // Base angle is opposite to Axis (pointing away from S)
            const baseAngle = Math.atan2(-dy, -dx)
            const angle = baseAngle + angleOffset * 0.8 // 0.8 to limit spread

            const startX = north.x + Math.cos(angle) * fanRadius
            const startY = north.y + Math.sin(angle) * fanRadius

            const path = MagneticField.traceFieldLine(startX, startY, 15, 300) // Increased length (150 -> 300)
            drawPath(ctx, path, north, south, false)
        }

        // South Fan (Inward)
        for (let i = 0; i < fanCount; i++) {
            const angleOffset = ((i / (fanCount - 1)) - 0.5) * Math.PI

            // Base angle is along Axis (pointing away from N)
            const baseAngle = Math.atan2(dy, dx)
            const angle = baseAngle + angleOffset * 0.8

            const startX = south.x + Math.cos(angle) * fanRadius
            const startY = south.y + Math.sin(angle) * fanRadius

            // Trace BACKWARD from S
            const path = MagneticField.traceFieldLine(startX, startY, -15, 300) // Increased length (150 -> 300)
            drawPath(ctx, path, north, south, true)
        }
    }

    // Helper to draw a full arc from N to S passing through (x,y)
    const drawContinuousLine = (ctx: CanvasRenderingContext2D, x: number, y: number, north: any, south: any) => {
        // Trace Forward towards South
        const pathF = MagneticField.traceFieldLine(x, y, 15)

        // Trace Backward towards North
        const pathB = MagneticField.traceFieldLine(x, y, -15)

        // Combine: Reverse B + Current + F
        // pathB starts at (x,y) and goes to N. reverse it to go N -> (x,y)
        const fullPath = [...pathB.reverse(), ...pathF]

        drawPath(ctx, fullPath, north, south, false)
    }

    const drawPath = (ctx: CanvasRenderingContext2D, path: { x: number, y: number }[], north: { x: number, y: number }, south: { x: number, y: number }, reverseArrows: boolean = false) => {
        if (path.length > 2) {
            ctx.beginPath()
            ctx.moveTo(path[0].x, path[0].y)

            for (let j = 1; j < path.length; j++) {
                ctx.lineTo(path[j].x, path[j].y)
            }

            // Gradient stroke logic
            const gradient = ctx.createLinearGradient(north.x, north.y, south.x, south.y)
            gradient.addColorStop(0, 'rgba(255, 60, 60, 0.7)')
            gradient.addColorStop(1, 'rgba(60, 60, 255, 0.7)')

            ctx.strokeStyle = gradient
            ctx.lineWidth = 3.0 // Increased thickness (1.5 -> 3.0)
            ctx.stroke()

            // Draw direction arrows
            // Only draw if path is long enough
            if (path.length > 8) {
                // Place arrow slightly away from start to avoid clutter at pole
                const arrowIndex = Math.min(6, path.length - 2)

                const p1 = path[arrowIndex]
                const p2 = path[arrowIndex + 1]

                if (p1 && p2) {
                    if (reverseArrows) {
                        // Pointing INTO the start point (S pole)
                        // Path[0] is at S pole. Path grows away.
                        // Vector P1 -> P2 is pointing AWAY from S.
                        // We want arrow pointing INTO S (P2 -> P1)
                        drawArrow(ctx, p2.x, p2.y, p1.x, p1.y)
                    } else {
                        // Pointing AWAY from start point (N pole)
                        // Path[0] is at N pole. Path grows away.
                        // Vector P1 -> P2 is pointing AWAY from N (Correct direction of B)
                        drawArrow(ctx, p1.x, p1.y, p2.x, p2.y)
                    }
                }
            }
        }
    }

    const drawGuides = (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)'
        ctx.lineWidth = 2
        ctx.setLineDash([10, 10])

        // Center crosshair
        ctx.beginPath()
        ctx.moveTo(w / 2 - 20, h / 2)
        ctx.lineTo(w / 2 + 20, h / 2)
        ctx.moveTo(w / 2, h / 2 - 20)
        ctx.lineTo(w / 2, h / 2 + 20)
        ctx.stroke()

        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
        ctx.font = '16px Arial'
        const text = 'Giữ nam châm trước camera (Đầu Đỏ & Xanh rõ ràng)'
        const textWidth = ctx.measureText(text).width
        ctx.fillText(text, w / 2 - textWidth / 2, h / 2 + 50)
        ctx.setLineDash([])
    }

    // Main tracking loop
    const startTracking = () => {
        const processFrame = () => {
            if (!videoRef.current || !canvasRef.current || !overlayCanvasRef.current) return

            const video = videoRef.current
            const canvas = canvasRef.current
            const overlayCanvas = overlayCanvasRef.current
            const ctx = canvas.getContext('2d', { willReadFrequently: true })
            const overlayCtx = overlayCanvas.getContext('2d')

            if (!ctx || !overlayCtx) return

            // Set canvas size to match video
            if (canvas.width !== video.videoWidth) {
                canvas.width = video.videoWidth
                canvas.height = video.videoHeight
                overlayCanvas.width = video.videoWidth
                overlayCanvas.height = video.videoHeight
            }

            // Draw video frame to processing canvas
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

            // 1. Detect color
            const magnetPos = ColorTracker.detect(ctx, canvas.width, canvas.height)

            // Clear overlay
            overlayCtx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height)

            if (magnetPos.isValid && magnetPos.north && magnetPos.south) {
                setMagnetDetected(true)

                // --- SMOOTHING LOGIC ---
                let smoothN = magnetPos.north
                let smoothS = magnetPos.south

                if (prevMagnetData.current.initialized) {
                    const smoothFactor = 0.2 // Lower = smoother but more lag
                    smoothN = {
                        x: lerp(prevMagnetData.current.north.x, magnetPos.north.x, smoothFactor),
                        y: lerp(prevMagnetData.current.north.y, magnetPos.north.y, smoothFactor)
                    }
                    smoothS = {
                        x: lerp(prevMagnetData.current.south.x, magnetPos.south.x, smoothFactor),
                        y: lerp(prevMagnetData.current.south.y, magnetPos.south.y, smoothFactor)
                    }
                } else {
                    prevMagnetData.current.initialized = true
                }

                // Save for next frame
                prevMagnetData.current.north = smoothN
                prevMagnetData.current.south = smoothS

                // Update Physics Engine with SMOOTH positions
                MagneticField.updatePoles(smoothN, smoothS)

                // 2. Draw Poles Indicators
                // North Pole (Red)
                overlayCtx.beginPath()
                overlayCtx.arc(smoothN.x, smoothN.y, 40, 0, Math.PI * 2) // Radius 30 -> 40
                overlayCtx.fillStyle = 'rgba(255, 50, 50, 0.9)'
                overlayCtx.fill()
                overlayCtx.strokeStyle = 'white'
                overlayCtx.lineWidth = 3
                overlayCtx.stroke()
                overlayCtx.fillStyle = 'white'
                overlayCtx.font = 'bold 32px Arial' // Font 24px -> 32px
                overlayCtx.textAlign = 'center'
                overlayCtx.textBaseline = 'middle'
                overlayCtx.fillText('N', smoothN.x, smoothN.y + 3) // Adjusted position

                // South Pole (Blue)
                overlayCtx.beginPath()
                overlayCtx.arc(smoothS.x, smoothS.y, 40, 0, Math.PI * 2) // Radius 30 -> 40
                overlayCtx.fillStyle = 'rgba(50, 50, 255, 0.9)'
                overlayCtx.fill()
                overlayCtx.stroke()
                overlayCtx.fillStyle = 'white'
                overlayCtx.fillText('S', smoothS.x, smoothS.y + 3)

                // Draw Magnet Axis
                overlayCtx.beginPath()
                overlayCtx.moveTo(smoothN.x, smoothN.y)
                overlayCtx.lineTo(smoothS.x, smoothS.y)
                overlayCtx.strokeStyle = 'rgba(255, 255, 255, 0.5)'
                overlayCtx.setLineDash([5, 5])
                overlayCtx.stroke()
                overlayCtx.setLineDash([])

                // 3. Draw Field Lines
                if (showFieldLines) {
                    drawFieldLines(overlayCtx, smoothN, smoothS)
                }

            } else {
                setMagnetDetected(false)
                prevMagnetData.current.initialized = false // Reset smoothing if lost

                // Draw calibration guides if not detected
                if (isRunning) {
                    drawGuides(overlayCtx, overlayCanvas.width, overlayCanvas.height)
                }
            }

            animationFrameRef.current = requestAnimationFrame(processFrame)
        }

        processFrame()
    }

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopCamera()
        }
    }, [])

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900">
            <Navigation />

            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-white mb-2">
                        🧲 Magnetic Field AR
                    </h1>
                    <p className="text-white/70">
                        Thực tế ảo - Đường sức từ của nam châm
                    </p>
                </div>

                {/* Main Content */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Video Feed (Expanded to 3 columns) */}
                    <div className="lg:col-span-3">
                        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
                            <div className="relative aspect-video rounded-xl overflow-hidden bg-black">
                                {/* Video element */}
                                <video
                                    ref={videoRef}
                                    autoPlay
                                    playsInline
                                    className="absolute inset-0 w-full h-full object-cover"
                                />

                                {/* Processing canvas (hidden) */}
                                <canvas
                                    ref={canvasRef}
                                    className="hidden"
                                />

                                {/* Overlay canvas for field lines */}
                                <canvas
                                    ref={overlayCanvasRef}
                                    className="absolute inset-0 w-full h-full"
                                />

                                {/* Magnet indicator */}
                                {magnetDetected && (
                                    <div className="absolute top-4 right-4 bg-green-500/80 px-3 py-1 rounded-full text-white text-sm font-bold animate-pulse">
                                        ⚡ Đã phát hiện nam châm
                                    </div>
                                )}
                            </div>

                            {/* Controls */}
                            <div className="mt-4 flex gap-3">
                                {!isRunning ? (
                                    <button
                                        onClick={startCamera}
                                        className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 rounded-lg text-white font-bold transition-all shadow-lg text-lg"
                                    >
                                        <Play className="w-6 h-6" />
                                        Bắt đầu AR
                                    </button>
                                ) : (
                                    <button
                                        onClick={stopCamera}
                                        className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 rounded-lg text-white font-bold transition-all shadow-lg text-lg"
                                    >
                                        <Pause className="w-6 h-6" />
                                        Dừng lại
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Controls Panel (Narrower - 1 column) */}
                    <div className="space-y-6">
                        {/* Settings */}
                        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
                            <h3 className="text-white font-bold mb-4 flex items-center gap-2 text-lg">
                                <Settings className="w-5 h-5" />
                                Cài đặt
                            </h3>

                            {/* Field Lines Toggle */}
                            <div className="mb-6">
                                <label className="flex items-center gap-3 text-white cursor-pointer group">
                                    <div className={`w-10 h-6 flex items-center bg-gray-600 rounded-full p-1 duration-300 ease-in-out ${showFieldLines ? 'bg-green-500' : ''}`}>
                                        <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out ${showFieldLines ? 'translate-x-4' : ''}`} />
                                    </div>
                                    <input
                                        type="checkbox"
                                        checked={showFieldLines}
                                        onChange={(e) => setShowFieldLines(e.target.checked)}
                                        className="hidden"
                                    />
                                    <span className="font-medium">Hiện đường sức từ</span>
                                </label>
                            </div>

                            {/* Field Line Density */}
                            <div className="mb-4">
                                <label className="block text-white text-sm mb-2 font-medium">
                                    Mật độ đường sức: {fieldLineDensity}
                                </label>
                                <input
                                    type="range"
                                    min="10"
                                    max="50"
                                    value={fieldLineDensity}
                                    onChange={(e) => setFieldLineDensity(parseInt(e.target.value))}
                                    className="w-full accent-blue-500 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                                />
                                <div className="flex justify-between text-xs text-white/50 mt-1">
                                    <span>Thưa</span>
                                    <span>Dày</span>
                                </div>
                            </div>
                        </div>

                        {/* Instructions */}
                        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
                            <h3 className="text-white font-bold mb-4 flex items-center gap-2 text-lg">
                                <Info className="w-5 h-5" />
                                Hướng dẫn
                            </h3>

                            <ol className="text-white/80 text-sm space-y-3 list-decimal list-inside marker:text-blue-400">
                                <li className="pl-1">Nhấn <b>"Bắt đầu AR"</b> để mở camera.</li>
                                <li className="pl-1">Đưa <b>nam châm</b> (loại có cực từ Đỏ/Xanh) vào khung hình.</li>
                                <li className="pl-1">Giữ nam châm thẳng hàng để hệ thống nhận diện tốt nhất.</li>
                                <li className="pl-1">Di chuyển nam châm để quan sát biến đổi của từ trường.</li>
                            </ol>
                        </div>

                        {/* Info */}
                        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
                            <h3 className="text-white font-bold mb-3 text-lg">Trạng thái</h3>
                            <div className="space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-white/70">Camera:</span>
                                    <span className={cameraReady ? "text-green-400 font-bold" : "text-red-400"}>
                                        {cameraReady ? 'Sẵn sàng' : 'Chưa bật'}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-white/70">Nam châm:</span>
                                    <span className={magnetDetected ? "text-green-400 font-bold" : "text-yellow-400"}>
                                        {magnetDetected ? 'Đã tìm thấy' : 'Đang tìm...'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default MagneticFieldAR
